'use client';
import { useState } from 'react';

import { localesActions, modifyLinkIfSelected } from '@/lib/i18n/utils';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import HeaderItemListSB from '@/components/atoms/layout/header/HeaderItemListSB';
import {
  HeaderProps,
  HeaderSBProps,
} from '@/components/atoms/layout/header/HeaderSB';
import LinkButton from '@/components/atoms/linkButton';
import LinkButtonSB from '@/components/atoms/linkButton/LinkSB';
import Popover from '@/components/atoms/popover';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { ImageLinkStrapi } from '@/components/molecules/imageLink/ImageLinkStrapi';

import IconTranslate from '~/svg/IconTranslate.svg';

export interface HeaderMainSBProps {
  firstLevels: HeaderProps['header']['firstLevels'];
  logo: ImageSBProps;
  logoLink?: HeaderSBProps['data']['logoLink'];
  logoAlignment?: HeaderSBProps['data']['logoAlignment'];
  rightSection?: HeaderSBProps['data']['extraSectionLinks'];
  showLanguages: boolean;
}

export type HeaderMainSBExtraProps = {
  headerRef: React.RefObject<HTMLDivElement | null>;
};

export default function HeaderMainSB({
  block,
  headerRef,
}: BlockComponentModel<HeaderMainSBProps> & HeaderMainSBExtraProps) {
  const {
    firstLevels,
    logo,
    logoLink,
    logoAlignment,
    rightSection,
    showLanguages,
  } = block;
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>();

  function handleMenuClick(index: number) {
    if (index === selectedMenuIndex) {
      handleMenuClose();
      return;
    }

    setSelectedMenuIndex(index);
  }

  function handleMenuClose() {
    setSelectedMenuIndex(undefined);
  }

  return (
    <div data-tracker-component='menu-desktop' className='w-full'>
      <div
        className={cn(
          'flex w-full justify-between',
          logoAlignment === 'center' && 'justify-center'
        )}
      >
        <div className='flex items-center gap-9'>
          {logoLink ? (
            <ImageLinkStrapi
              inner={{ classNames: { image: 'max-w-[150px] h-10' } }}
              block={{ image: logo, link: { url: logoLink, text: '' } }}
            />
          ) : (
            <ImageSB
              block={logo}
              inner={{ classNames: { image: 'max-w-[150px] h-10' } }}
            />
          )}
          {firstLevels.length > 0 && (
            <div className='flex gap-6'>
              {firstLevels.map((item, index) => {
                return (
                  <HeaderItemListSB
                    key={index}
                    block={{ firstLevel: item }}
                    hasMenuOpen={selectedMenuIndex === index}
                    onMenuClick={() => handleMenuClick(index)}
                    onClose={() => handleMenuClose()}
                    headerRef={headerRef}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div className='flex items-center gap-6'>
          {showLanguages && (
            <Popover
              classNames={{ childrenWrapper: 'dark w-max' }}
              position='bottom'
              target={
                <Button
                  color='tertiary'
                  variant='text'
                  size='s'
                  slim
                  classNames={{
                    root: 'text-neutral-N40 hover:text-neutral-N00 font-sans',
                  }}
                >
                  <IconTranslate />
                </Button>
              }
            >
              <div className='flex flex-col gap-4'>
                {Object.entries(localesActions()).map(([key, value]) => {
                  const customSelectedLink = modifyLinkIfSelected({
                    link: value.link,
                    text: value.text,
                  });

                  if (customSelectedLink) {
                    return customSelectedLink;
                  }

                  return (
                    <LinkButton
                      key={key}
                      href={value.link}
                      variant='text'
                      color='secondary'
                      slim
                      underline={false}
                      classNames={{
                        root: 'text-neutral-dark-N40 hover:text-neutral-dark-N00',
                      }}
                    >
                      {value.text}
                    </LinkButton>
                  );
                })}
              </div>
            </Popover>
          )}
          {rightSection?.map((item, index) => (
            <LinkButtonSB
              key={index}
              block={item}
              inner={{
                underline: false,
                size: 's',
                onClick: () => setSelectedMenuIndex(undefined),
                ...(item.variant === 'text' && item.color === 'neutral'
                  ? {
                      slim: true,
                    }
                  : ''),
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
